import moment from "moment-timezone";

/** Default line + three buy steps (½, 1, 1½ points). */
export const BUY_POINT_STEPS = [0, 0.5, 1, 1.5];

/** American odds penalty per 0.5 point bought (legacy ladder). */
export const BUY_POINT_PRICE_STEP = 10;

const MATCH_TZ = "America/Chicago";

const roundPoint = (n) => Math.round(Number(n) * 10) / 10;

const formatBuyStepAmount = (step) => {
  if (step === 0.5) return "½";
  if (step === 1.5) return "1½";
  return String(step);
};

const formatBuyPointPrice = (price) => {
  const p = Number(price);
  if (!Number.isFinite(p)) return "";
  return p > 0 ? `(+${p})` : `(${p})`;
};

/**
 * Admin API (`get-Game-ForPoints`) returns AgentgameSetting rows for a league.
 * Buy-points UI is shown only when that league is configured with buyPointsStatus.
 */
export function isBuyPointsEnabledForSport(apiResult, sportTitle) {
  if (!sportTitle || !Array.isArray(apiResult) || apiResult.length === 0) {
    return false;
  }
  const normalized = String(sportTitle).trim().toLowerCase();
  return apiResult.some((row) => {
    const titleMatch =
      String(row?.title || "")
        .trim()
        .toLowerCase() === normalized;
    return (
      titleMatch &&
      row?.buyPointsStatus !== false &&
      row?.status !== false
    );
  });
}

/**
 * Buy points are pre-game only — blocked once kickoff has passed.
 * Uses the same kickoff clock as the bet board live badge (not the 30-min pre-live odds window).
 */
export function isGameEligibleForBuyPoints(gameTime) {
  if (!gameTime) return false;
  const matchMoment = moment.tz(gameTime, MATCH_TZ);
  if (!matchMoment.isValid()) return false;
  return matchMoment.diff(moment(), "minutes") > 0;
}

export function isSpreadOrTotalMarket(item) {
  const t = item?.type || item?.oddType;
  return t === "spreads" || t === "totals";
}

export function hasStraightBuyPointsPurchase(item) {
  if (!item || !isSpreadOrTotalMarket(item)) return false;
  if (item.isCallBetType === true || item.isCallBetType === "true") return false;
  if (item.isCallBetMode === true) return false;

  const bp = Number(item.buyPoints);
  if (Number.isFinite(bp) && bp > 0) return true;

  const orig = Number(item.originalPoint ?? item.bookPoint);
  const custom = Number(item.customizedPoint ?? item.point);
  return (
    Number.isFinite(orig) &&
    Number.isFinite(custom) &&
    orig !== custom
  );
}

/** True when admin API confirms buy points are enabled for this league title. */
export function isBuyPointsAdminEnabled(apiStatus, apiResult, sportTitle) {
  return (
    apiStatus === true && isBuyPointsEnabledForSport(apiResult, sportTitle)
  );
}

export function resolveBuyPointsForSlipItem(apiStatus, apiResult, sportTitle, slipItem) {
  const adminEnabled = isBuyPointsAdminEnabled(apiStatus, apiResult, sportTitle);
  return resolveBuyPointsEnabledForSlipItem(adminEnabled, slipItem);
}

/** Lookup refreshed admin map (key = lowercased sport_title). */
export function isBuyPointsEnabledInAdminMap(buyPointsMap, sportTitle) {
  if (!sportTitle || !buyPointsMap) return false;
  return buyPointsMap[String(sportTitle).trim().toLowerCase()] === true;
}

/** Admin + sport rules + pre-game guard for showing buy-points dropdown on a slip leg. */
export function canShowBuyPointsUI(item, buyPointsMap = null) {
  if (!item || !isSpreadOrTotalMarket(item)) return false;

  const adminOk =
    buyPointsMap != null
      ? isBuyPointsEnabledInAdminMap(buyPointsMap, item.sport_title)
      : item.buypoint === true;
  if (!adminOk) return false;

  if (!isGameEligibleForBuyPoints(item.gameTime || item.commence_time)) {
    return false;
  }
  return buildBuyPointSelectOptions(item).length > 0;
}

/** After admin API check — disable on live games even when league allows buy points. */
export function resolveBuyPointsEnabledForSlipItem(adminEnabled, item) {
  if (!adminEnabled || !item) return false;
  if (!isSpreadOrTotalMarket(item)) return false;
  return isGameEligibleForBuyPoints(item.gameTime || item.commence_time);
}

function flattenSlipLegs(legs) {
  if (!Array.isArray(legs) || legs.length === 0) return [];
  if (Array.isArray(legs[0])) return legs.flat();
  return legs;
}

/** Client-side guard before submit — complements backend rejection. */
export function validateBuyPointsSlipBeforeSubmit(legs, betType) {
  const normalizedType = String(betType || "").toLowerCase();
  if (normalizedType.includes("teaser")) {
    return { ok: true };
  }

  for (const item of flattenSlipLegs(legs)) {
    if (!hasStraightBuyPointsPurchase(item)) continue;
    if (isGameEligibleForBuyPoints(item.gameTime || item.commence_time)) {
      continue;
    }
    const label = item.teamName || item.homeTeam || "this selection";
    return {
      ok: false,
      message: `Buy points are not allowed on live or in-progress games (${label}). Use the book line or pick a pre-game match.`,
    };
  }

  return { ok: true };
}

/**
 * Build buy-points ladder for spreads/totals from the book line + price on the slip.
 * Returns `{ point, price, label, value, buyDelta }` options for the bet-slip select.
 */
export function buildBuyPointSelectOptions(item) {
  if (!item) return [];

  const basePoint = Number(item.originalPoint ?? item.bookPoint ?? item.point);
  const basePrice = Number(
    item.bookPrice ?? item.originalPrice ?? item.price
  );
  if (!Number.isFinite(basePoint) || !Number.isFinite(basePrice)) return [];

  return BUY_POINT_STEPS.map((step, index) => {
    const point = roundPoint(basePoint + step);
    const price = basePrice - (step / 0.5) * BUY_POINT_PRICE_STEP;
    const label =
      step === 0
        ? "Buy Points!"
        : `Buy ${formatBuyStepAmount(step)} point ${formatBuyPointPrice(price)}`;

    return {
      value: index,
      point,
      price,
      label,
      buyDelta: step,
    };
  });
}

export function findBuyPointOptionIndex(item, options = []) {
  if (!options.length) return 0;

  const buyDelta = Number(item?.buyPoints);
  if (Number.isFinite(buyDelta)) {
    const byDelta = options.findIndex(
      (o) => Number(o.buyDelta) === buyDelta
    );
    if (byDelta >= 0) return byDelta;
  }

  const currentPoint = Number(item?.customizedPoint ?? item?.point);
  const currentPrice = Number(item?.price);
  const idx = options.findIndex(
    (o) => Number(o.point) === currentPoint && Number(o.price) === currentPrice
  );
  return idx >= 0 ? idx : 0;
}
